import { auth } from '@/firebase/firebase';
import type { DesktopFeed, DesktopFeeds } from '@/services/desktopConnectionApi/desktopConnectionApiSchemas';
import { errorReporter } from '@/services/errorReportingService';
import { isValidVersion } from '@/utils/versionGate';

/**
 * Published desktop versions, read from the auto-update feed manifests on
 * downloads.notego.ai — the same files installed desktop apps poll.
 *
 * notego_electron's `release:mac` / `release:win` publish the stable feeds and
 * their `:beta` variants the beta ones, so reading them here means nobody has to
 * retype a version in admin after a release, and each platform shows its own
 * version (Mac and Windows ship on different days).
 *
 * The feed host sends no CORS headers, so the browser can't read the manifests
 * itself — the API reads them server-side (`GET /desktop-feeds`) and this maps
 * each one to the download field it belongs to.
 */
export type FeedField =
  | 'mac_arm_url'
  | 'mac_intel_url'
  | 'windows_intel_url'
  | 'windows_arm_url'
  | 'mac_arm_beta_url'
  | 'mac_intel_beta_url'
  | 'windows_intel_beta_url'
  | 'windows_arm_beta_url';

/** Published version per build, e.g. `{ mac_arm_url: '1.2.64' }`. A build with no readable feed is absent. */
export type DesktopFeedVersions = Partial<Record<FeedField, string>>;

const API_URL = import.meta.env.VITE_API_URL;

/** Keyed `${channel}/${platform}/${arch}`, matching the feed paths notego_electron publishes. */
const FIELD_BY_FEED: Record<string, FeedField> = {
  'stable/mac/arm64': 'mac_arm_url',
  'stable/mac/x64': 'mac_intel_url',
  'stable/win/x64': 'windows_intel_url',
  'stable/win/arm64': 'windows_arm_url',
  'beta/mac/arm64': 'mac_arm_beta_url',
  'beta/mac/x64': 'mac_intel_beta_url',
  'beta/win/x64': 'windows_intel_beta_url',
  'beta/win/arm64': 'windows_arm_beta_url',
};

// The whole settings page waits on this, so a hung API must not hang it. Longer
// than the API's own per-feed timeout so a slow feed comes back as missing, not
// as the whole read failing.
const FEED_TIMEOUT_MS = 8000;

const toFeedVersions = (feeds: DesktopFeed[]): DesktopFeedVersions =>
  feeds.reduce<DesktopFeedVersions>((versions, feed) => {
    const field = FIELD_BY_FEED[`${feed.channel}/${feed.platform}/${feed.arch}`];
    if (field && typeof feed.version === 'string' && isValidVersion(feed.version)) {
      versions[field] = feed.version;
    }
    return versions;
  }, {});

/**
 * Read every build's published version from the update feeds.
 *
 * Never throws: when the feeds can't be read (signed out, offline, API down,
 * timed out) the result is empty and callers fall back to the admin-entered
 * `latest_version`.
 */
export const fetchDesktopFeedVersions = async (): Promise<DesktopFeedVersions> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      return {};
    }

    const response = await fetch(`${API_URL}/desktop-feeds`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(FEED_TIMEOUT_MS),
      cache: 'no-store',
      redirect: 'error',
    });
    if (!response.ok) {
      throw new Error(`Desktop feeds unavailable (HTTP ${response.status})`);
    }

    const data = (await response.json()) as Partial<DesktopFeeds> | null;
    return Array.isArray(data?.feeds) ? toFeedVersions(data.feeds) : {};
  } catch (error) {
    errorReporter.expected('Failed to read desktop update feeds', error, {
      feature: 'settings',
      operation: 'fetchDesktopFeedVersions',
    });
    return {};
  }
};
